import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import QRCodeLib from 'qrcode';
import { 
  FaBox,
  FaUpload, 
  FaDownload, 
  FaSearch, 
  FaFilter, 
  FaTrash, 
  FaEye,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
  FaSpinner,
  FaWifi,
  FaServer,
  FaExclamationCircle,
  FaInfoCircle,
  FaQrcode
} from 'react-icons/fa';

export default function Items() {
  // Series options for react-select
  const seriesOptions = [
    { value: 'RS', label: 'RS - Regular Series' },
    { value: 'AO', label: 'AO - Alternative Option' },
    { value: 'SC', label: 'SC - Special Category' },
    { value: 'DC', label: 'DC - Deluxe Category' },
    { value: 'ITF', label: 'ITF - Import Trade Format' },
    { value: 'OT', label: 'OT - Other' }
  ];

  // Custom styles for react-select to match the existing theme
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: '1px solid #d1d5db',
      borderRadius: '0.5rem',
      padding: '0.5rem',
      backgroundColor: 'white',
      boxShadow: state.isFocused ? '0 0 0 2px #3b82f6' : 'none',
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
      },
      minHeight: '48px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
        ? '#eff6ff' 
        : 'white',
      color: state.isSelected ? 'white' : '#374151',
      padding: '12px 16px',
      fontSize: '14px',
      '&:hover': {
        backgroundColor: state.isSelected ? '#3b82f6' : '#eff6ff'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontSize: '14px'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#374151',
      fontSize: '14px'
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e5e7eb'
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '4px',
      borderRadius: '0.5rem'
    })
  };

  // Dark mode support (reads from localStorage, live-synced with layout toggle)
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // react-select dark theme
  const darkSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: '1px solid #4b5563',
      borderRadius: '0.5rem',
      padding: '0.5rem',
      backgroundColor: '#1f2937',
      boxShadow: state.isFocused ? '0 0 0 2px #3b82f6' : 'none',
      borderColor: state.isFocused ? '#3b82f6' : '#4b5563',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#6b7280'
      },
      minHeight: '48px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#3b82f6'
        : state.isFocused
        ? '#374151'
        : '#1f2937',
      color: state.isSelected ? 'white' : '#e5e7eb',
      padding: '12px 16px',
      fontSize: '14px',
      '&:hover': {
        backgroundColor: state.isSelected ? '#3b82f6' : '#374151'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
      fontSize: '14px'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#e5e7eb',
      fontSize: '14px'
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
      border: '1px solid #4b5563',
      backgroundColor: '#1f2937'
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '4px',
      borderRadius: '0.5rem'
    })
  };

  const [items, setItems] = useState([]);
  // Table vs Tree (Item -> Variants -> Sub-items) view
  const [viewMode, setViewMode] = useState('table');
  const [expandedItems, setExpandedItems] = useState({});
  const toggleItemExpanded = (id) => setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showBulkQRModal, setShowBulkQRModal] = useState(false);
  const [bulkQRFilters, setBulkQRFilters] = useState({ brand: null, category: null });
  const [bulkQRItems, setBulkQRItems] = useState([]);
  const [loadingBulkQR, setLoadingBulkQR] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    count: 0,
    totalItems: 0
  });
  
  // Toast notification system
  const [toast, setToast] = useState({
    show: false,
    type: 'info', // success, error, warning, info
    title: '',
    message: '',
    details: null
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    brand: '',
    category: '',
    series: '',
    page: 1,
    limit: 50
  });
  
  // Import modal states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importForm, setImportForm] = useState({
    file: null,
    brandId: null,
    categoryId: null,
    employeeId: null,
  series: seriesOptions[0], // Default to first option (RS)
  applyGstToOffers: false,
  gstOfferTypes: ['clp', 'sdp', 'nrp', 'kap', 'hvr', 'spl']
  });
  const [importResults, setImportResults] = useState(null);
  const [strictImport, setStrictImport] = useState(false);
  const [previewChecked, setPreviewChecked] = useState(false);
  
  // Enhanced import progress tracking
  const [importProgress, setImportProgress] = useState({
    show: false,
    progress: 0,
    message: '',
    currentItem: '',
    processedCount: 0,
    totalCount: 0,
    startTime: null,
    timeElapsed: 0,
    eta: 0,
    throughput: 0
  });
  
  // Import results modal
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [detailedResults, setDetailedResults] = useState(null);
  
  // Excel preview state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [previewViewMode, setPreviewViewMode] = useState('flat');
  
  // Dropdown data
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Brand offer types (fetched dynamically)
  const [brandOfferTypes, setBrandOfferTypes] = useState([]);
  
  // Template download state
  const [templateBrand, setTemplateBrand] = useState(null);
  const [templateCategory, setTemplateCategory] = useState(null);
  const [templateCategoryOptions, setTemplateCategoryOptions] = useState([]);
  const [showBrandSelection, setShowBrandSelection] = useState(false);
  // Which action the brand-selection modal drives: 'template' | 'export'
  const [brandModalMode, setBrandModalMode] = useState('template');
  // Id of the item currently being (de)activated
  const [activeTogglingId, setActiveTogglingId] = useState(null);
  
  const fileInputRef = useRef(null);

  // Modal state for showing details
  const [modalOpen, setModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [expandedUpdates, setExpandedUpdates] = useState({});

  // Toggle function for expanding update details
  const toggleUpdateDetails = (updateId) => {
    setExpandedUpdates(prev => ({
      ...prev,
      [updateId]: !prev[updateId]
    }));
  };

  // Close modal and reset states
  const closeModal = () => {
    setModalOpen(false);
    setExpandedUpdates({});
  };

  // Helper function to check if a value is meaningful (not empty, zero, or '00000')
  const isValidValue = (value) => {
    if (!value) return false;
    if (value === null || value === undefined) return false;
    const stringValue = String(value).trim();
    if (stringValue === '' || stringValue === '0' || stringValue === '00000' || stringValue === 'null' || stringValue === 'undefined') return false;
    if (Number(value) === 0) return false;
    return true;
  };

  // Helper function to display value or fallback
  const displayValue = (value, fallback = 'N/A') => {
    return isValidValue(value) ? value : fallback;
  };

  // Toast notification functions
  const showToast = (type, title, message, details = null) => {
    setToast({
      show: true,
      type,
      title,
      message,
      details
    });
    
    // Auto-hide success and info toasts after 5 seconds
    if (type === 'success' || type === 'info') {
      setTimeout(() => {
        setToast(prev => ({ ...prev, show: false }));
      }, 5000);
    }
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  // Enhanced error handling function
  const handleApiError = (error, context = '') => {
    console.error(`API Error ${context}:`, error);
    
    let title, message, details;
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      title = 'Connection Error';
      message = 'Unable to connect to the server. Please check your internet connection.';
      details = 'This usually happens when the server is down or your internet connection is unstable.';
    } else if (error.status === 404) {
      title = 'Resource Not Found';
      message = 'The requested resource could not be found on the server.';
      details = 'This might be a temporary issue. Please try refreshing the page or contact support.';
    } else if (error.status === 500) {
      title = 'Server Error';
      message = 'An internal server error occurred while processing your request.';
      details = 'Please try again in a few moments. If the problem persists, contact support.';
    } else if (error.status === 403) {
      title = 'Access Denied';
      message = 'You do not have permission to perform this action.';
      details = 'Please contact your administrator if you believe this is an error.';
    } else if (error.status === 401) {
      title = 'Authentication Required';
      message = 'Your session has expired. Please log in again.';
      details = 'For security reasons, you need to re-authenticate to continue.';
    } else if (error.status >= 400 && error.status < 500) {
      title = 'Request Error';
      message = error.message || 'There was an error with your request.';
      details = 'Please check your input and try again.';
    } else {
      title = 'Unexpected Error';
      message = error.message || 'An unexpected error occurred.';
      details = 'Please try again. If the problem persists, contact support.';
    }
    
    showToast('error', title, message, details);
  };

  // Load dropdown data
  useEffect(() => {
    fetchBrands();
    fetchCategories();
    fetchEmployees();
  }, []);

  // Load items when filters change
  useEffect(() => {
    fetchItems();
  }, [filters]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/items?${queryParams}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setItems(data.data);
        setPagination(data.pagination);
        
        if (data.data.length === 0 && filters.search) {
          showToast('info', 'No Results Found', 
            `No items found matching "${filters.search}". Try adjusting your search criteria.`);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch items');
      }
    } catch (error) {
      handleApiError(error, 'while fetching items');
      // Set empty state on error
      setItems([]);
      setPagination({ current: 1, total: 0, count: 0, totalItems: 0 });
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/items/brands`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `Failed to load brands: HTTP ${response.status}`);
        error.status = response.status;
        throw error;
      }
      
      const data = await response.json();
      if (data.success) {
        // Format data for react-select
        const formattedBrands = data.data.map(brand => ({
          value: brand._id,
          label: brand.name,
          advancedItems: !!brand.advancedItems,
          categories: (Array.isArray(brand.categories) ? brand.categories : [])
            .filter(c => c && (c._id || c))
            .map(c => ({ value: String(c._id || c), label: c.name || String(c) }))
        }));
        setBrands(formattedBrands);
      } else {
        throw new Error(data.message || 'Failed to fetch brands');
      }
    } catch (error) {
      handleApiError(error, 'while fetching brands');
      setBrands([]); // Set empty array on error
      showToast('warning', 'Brands Unavailable', 
        'Unable to load brand list. You can still import items, but brand filtering may not work properly.');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/items/categories`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `Failed to load categories: HTTP ${response.status}`);
        error.status = response.status;
        throw error;
      }
      
      const data = await response.json();
      if (data.success) {
        // Format data for react-select
        const formattedCategories = data.data.map(category => ({
          value: category._id,
          label: category.name
        }));
        setCategories(formattedCategories);
      } else {
        throw new Error(data.message || 'Failed to fetch categories');
      }
    } catch (error) {
      handleApiError(error, 'while fetching categories');
      setCategories([]); // Set empty array on error
      showToast('warning', 'Categories Unavailable', 
        'Unable to load category list. You can still import items, but category filtering may not work properly.');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/items/employees`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || `Failed to load employees: HTTP ${response.status}`);
        error.status = response.status;
        throw error;
      }
      
      const data = await response.json();
      if (data.success) {
        // Format data for react-select
        const formattedEmployees = data.data.map(employee => ({
          value: employee._id,
          label: `${employee.name} (${employee.id})`
        }));
        setEmployees(formattedEmployees);
      } else {
        throw new Error(data.message || 'Failed to fetch employees');
      }
    } catch (error) {
      handleApiError(error, 'while fetching employees');
      setEmployees([]); // Set empty array on error
      showToast('warning', 'Employees Unavailable', 
        'Unable to load employee list. Please select an employee from the available options.');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      ...(key === 'brand' ? { category: '' } : {}),
      page: 1 // Reset to first page when filters change
    }));
  };
  const reportCategoryOptions = filters.brand
    ? (brands.find(b => b.value === filters.brand)?.categories || [])
    : categories;

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        showToast('error', 'Invalid File Type', 
          'Please select a valid Excel file (.xlsx or .xls format only).', 
          'The selected file format is not supported. Make sure you are uploading an Excel file saved in the correct format.');
        event.target.value = ''; // Clear the input
        return;
      }
      
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        showToast('error', 'File Too Large', 
          'The selected file is too large. Please choose a file smaller than 10MB.', 
          'Large files may cause performance issues during import. Consider breaking your data into smaller files.');
        event.target.value = ''; // Clear the input
        return;
      }
      
      // Check if file has content
      if (file.size === 0) {
        showToast('error', 'Empty File', 
          'The selected file appears to be empty. Please choose a file with data.', 
          'Make sure your Excel file contains at least one row of data with the required columns.');
        event.target.value = ''; // Clear the input
        return;
      }
      
      setImportForm(prev => ({ ...prev, file }));
      setPreviewChecked(false);
      showToast('success', 'File Selected', 
        `${file.name} has been selected for import. File size: ${(file.size / 1024).toFixed(1)}KB`);
    }
  };

  const handleImport = async () => {
    // Comprehensive validation
    const validationErrors = [];
    
    if (!importForm.file) {
      validationErrors.push('Please select an Excel file to import.');
    }
    
    if (!importForm.brandId || !importForm.brandId.value) {
      validationErrors.push('Please select a brand for the items.');
    }
    
    if (!importForm.categoryId || !importForm.categoryId.value) {
      validationErrors.push('Please select a category for the items.');
    }

    if (!importForm.employeeId || !importForm.employeeId.value) {
      validationErrors.push('Please select an employee who is performing the import.');
    }

    if (!previewChecked) {
      validationErrors.push('Please check the Excel file using "Check Excel" button before importing.');
    }
    
    if (!importForm.series || !importForm.series.value) {
      validationErrors.push('Please select a series for the items.');
    }
    
    if (validationErrors.length > 0) {
      showToast('error', 'Validation Failed', 
        'Please fix the following issues before importing:', 
        validationErrors.join('\n• '));
      return;
    }

    setImportLoading(true);
    
    // Initialize progress tracking
    const startTime = new Date();
    setImportProgress({
      show: true,
      progress: 0,
      message: 'Preparing import...',
      currentItem: '',
      processedCount: 0,
      totalCount: 0,
      startTime,
      timeElapsed: 0,
      eta: 0,
      throughput: 0
    });

    const formData = new FormData();
    formData.append('file', importForm.file);
    formData.append('brandId', importForm.brandId.value);
    formData.append('categoryId', importForm.categoryId.value);
    formData.append('employeeId', importForm.employeeId.value);
    formData.append('series', importForm.series.value);
    formData.append('applyGstToOffers', importForm.applyGstToOffers ? 'true' : 'false');
    formData.append('strict', strictImport ? 'true' : 'false');
    if (importForm.applyGstToOffers && Array.isArray(importForm.gstOfferTypes)) {
      formData.append('gstOfferTypes', JSON.stringify(importForm.gstOfferTypes));
    }

    // Progress update interval
    const progressInterval = setInterval(() => {
      setImportProgress(prev => {
        const timeElapsed = (new Date() - prev.startTime) / 1000;
        const throughput = prev.processedCount > 0 ? prev.processedCount / timeElapsed : 0;
        const eta = prev.totalCount > 0 && throughput > 0 ? 
          Math.max(0, (prev.totalCount - prev.processedCount) / throughput) : 0;
        
        return {
          ...prev,
          timeElapsed,
          throughput,
          eta
        };
      });
    }, 1000);

    try {
      // Update progress
      setImportProgress(prev => ({
        ...prev,
        message: 'Uploading file and validating data...',
        progress: 10
      }));

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/items/import`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || `Server returned ${response.status}: ${response.statusText}`;
          
          // Handle validation errors specifically
          if (errorData.errorType === 'HEADER_VALIDATION') {
            showToast('error', 'Excel File Format Error', errorMessage, errorData.details);
          } else {
            showToast('error', 'Import Failed', errorMessage, errorData.details);
          }
        } catch {
          errorMessage = `Server returned ${response.status}: ${response.statusText}`;
          showToast('error', 'Import Failed', errorMessage);
        }
        return;
      }

      setImportProgress(prev => ({
        ...prev,
        message: 'Processing import results...',
        progress: 90
      }));

      const result = await response.json();
      setImportResults({ ...result.results, warnings: result.warnings || result.results?.warnings || [] });
      setDetailedResults(result.results);

      // Final progress update
      setImportProgress(prev => ({
        ...prev,
        message: 'Import completed successfully!',
        progress: 100,
        processedCount: result.results.total || 0,
        totalCount: result.results.total || 0
      }));

      // Show success message with detailed results
      const stats = result.results.processingStatistics || {};
      const breakdown = result.results.detailedBreakdown || {};
      
      const successMessage = `Import completed successfully!\n\n` +
        `📊 Results Summary:\n` +
        `• Total items processed: ${stats.totalRows || 0}\n` +
        `• Successful updates: ${stats.successfulUpdates || 0}\n` +
        `• Failed updates: ${stats.failedUpdates || 0}\n` +
        `• New items created: ${breakdown.newItems || 0}\n` +
        `• Existing items updated: ${breakdown.updatedItems || 0}\n\n` +
        `⏱️ Performance:\n` +
        `• Processing time: ${stats.processingTime || 'N/A'}\n` +
        `• Throughput: ${stats.throughput || 'N/A'}\n\n` +
        `Click "View Detailed Results" to see complete breakdown and download Excel reports.`;

      showToast('success', 'Import Completed', successMessage);
      
      // Close import modal and show results
      setShowImportModal(false);
      setShowResultsModal(true);
      
      // Refresh items list
      fetchItems();

    } catch (error) {
      console.error('Import error:', error);
      
      setImportProgress(prev => ({
        ...prev,
        message: 'Import failed due to network error',
        progress: 0
      }));

      handleApiError(error, 'during import');
    } finally {
      clearInterval(progressInterval);
      setImportLoading(false);
      
      // Hide progress modal after 3 seconds if successful
      setTimeout(() => {
        setImportProgress(prev => ({ ...prev, show: false }));
      }, 3000);
    }
  };

  // Function to download detailed Excel report
  const downloadImportReport = async () => {
    if (!detailedResults || !detailedResults.importId) {
      showToast('error', 'No Data', 'No import results available to export. Please re-run the import.');
      return;
    }

    try {
      showToast('info', 'Preparing Report', 'Generating detailed Excel report...');
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/items/generate-import-report/${detailedResults.importId}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to generate report');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `import-report-${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

showToast('success', 'Report Downloaded', 'Detailed Excel report has been downloaded successfully.');
       
     } catch (error) {
       console.error('Error downloading report:', error);
       showToast('error', 'Download Failed', error.message || 'Failed to generate Excel report.');
     }
   };

  // Function to fetch brand offer types
  const fetchBrandOfferTypes = async (brandId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/brands/${brandId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.offerTypes) {
          const activeTypes = data.data.offerTypes
            .filter(ot => ot.active !== false && ot.key !== 'flat')
            .map(ot => ({ key: ot.key, label: ot.label || ot.key.toUpperCase() }));
          setBrandOfferTypes(activeTypes);
          return activeTypes;
        }
      }
    } catch (e) {
      console.warn('Could not fetch brand offer types:', e);
    }
    setBrandOfferTypes([]);
    return [];
  };
  const previewExcelFile = async () => {
    if (!importForm.file) {
      showToast('error', 'No File Selected', 'Please select an Excel file first.');
      return;
    }
    if (!importForm.brandId || !importForm.brandId.value) {
      showToast('error', 'Brand Required', 'Please select a brand to validate against the correct template.');
      return;
    }

    try {
      showToast('info', 'Processing File', 'Reading and validating Excel file...');
      
      // Dynamic import xlsx library
      const XLSX = await import('xlsx');
      
      const file = importForm.file;
      const arrayBuffer = await file.arrayBuffer();

      // Inspect drawings so broken/non-standard image data is reported before import.
      let drawingNotice = '';
      try {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(arrayBuffer);
        const drawingFiles = Object.keys(zip.files).filter(n => /^xl\/drawings\/[^/]+\.xml$/.test(n));
        let repairable = false;
        let imageCount = 0;
        for (const name of drawingFiles) {
          const xml = await zip.file(name).async('string');
          imageCount += (xml.match(/<(?:xdr:)?(?:oneCellAnchor|twoCellAnchor)/g) || []).length;
          if (/<wsDr\b/.test(xml) && !/xmlns:xdr=/.test(xml)) repairable = true;
        }
        if (repairable) {
          drawingNotice = `Images use a non-standard drawing format (${imageCount} found). The importer will repair them and import the images. If repair fails, the brand/parent image will be used.`;
        }
      } catch (e) {
        // ignore inspection errors; the import itself will report them
      }
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      // Simple 3-tab format: Products / Variants / Accessories.
      const bundleNames = ['Products', 'Variants', 'Accessories'];
      if (bundleNames.some(name => workbook.SheetNames.includes(name))) {
        const readTab = (name) => {
          const sheet = workbook.Sheets[name];
          if (!sheet) return { headers: [], rows: [] };
          const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
          return {
            headers: (json[0] || []).map(h => String(h || '').trim()),
            rows: json.slice(1).filter(row => row.some(c => c !== '' && c !== null && c !== undefined))
          };
        };
        const products = readTab('Products');
        const variants = readTab('Variants');
        const accessories = readTab('Accessories');
        const issues = [];
        const num = (value) => Number(String(value ?? '').replace(/[₹,\s]/g, ''));

        products.rows.forEach((row, index) => {
          const rowNum = index + 2;
          const get = (name) => { const col = products.headers.indexOf(name); return col >= 0 ? row[col] : ''; };
          const desc = String(get('SKU Description') || get('Product Name') || '').trim();
          if (!desc) issues.push(`Products row ${rowNum}: Product description is required`);
          const gst = num(get('GST Rate'));
          const mrp = num(get('MRP'));
          if (!Number.isFinite(gst) || gst < 0 || gst > 100) issues.push(`Products row ${rowNum}: Invalid GST Rate (0-100)`);
          if (!Number.isFinite(mrp) || mrp <= 0) issues.push(`Products row ${rowNum}: Invalid MRP (must be > 0)`);
        });
        variants.rows.forEach((row, index) => {
          const rowNum = index + 2;
          const get = (name) => { const col = variants.headers.indexOf(name); return col >= 0 ? row[col] : ''; };
          const parent = String(get('Parent SKU') || '').trim();
          const name = String(get('Variant') || get('Variant Name') || '').trim();
          const mrp = num(get('MRP'));
          if (!parent) issues.push(`Variants row ${rowNum}: Parent SKU is required`);
          if (!name && !String(get('Variant ID') || '').trim()) issues.push(`Variants row ${rowNum}: Variant name or ID is required`);
          if (!Number.isFinite(mrp) || mrp <= 0) issues.push(`Variants row ${rowNum}: Invalid MRP (must be > 0)`);
        });
        accessories.rows.forEach((row, index) => {
          const rowNum = index + 2;
          const get = (name) => { const col = accessories.headers.indexOf(name); return col >= 0 ? row[col] : ''; };
          if (!String(get('Parent SKU') || '').trim()) issues.push(`Accessories row ${rowNum}: Parent SKU is required`);
          if (!String(get('Accessory SKU') || get('Child SKU') || '').trim()) issues.push(`Accessories row ${rowNum}: Accessory SKU is required`);
          const qty = get('Qty');
          if (qty !== '' && qty !== undefined && qty !== null) {
            const n = num(qty);
            if (!Number.isFinite(n) || n < 1 || !Number.isInteger(n)) issues.push(`Accessories row ${rowNum}: Qty must be a whole number >= 1`);
          }
        });

        const headers = ['Sheet', ...Array.from(new Set([
          ...products.headers, ...variants.headers, ...accessories.headers
        ].filter(Boolean)))];
        const toPreviewRow = (sheetName, tab, row, index) => ({
          rowNum: index + 2,
          data: { Sheet: sheetName, ...Object.fromEntries(tab.headers.map((h, col) => [h, row[col]])) }
        });
        const previewRows = [
          ...products.rows.slice(0, 3).map((row, i) => toPreviewRow('Products', products, row, i)),
          ...variants.rows.slice(0, 3).map((row, i) => toPreviewRow('Variants', variants, row, i)),
          ...accessories.rows.slice(0, 3).map((row, i) => toPreviewRow('Accessories', accessories, row, i))
        ];

        setPreviewErrors(issues);
        setPreviewData({
          drawingNotice,
          headers,
          totalRows: products.rows.length + variants.rows.length + accessories.rows.length,
          previewRows,
          groups: [],
          missingHeaders: [],
          missingAdditionalHeaders: [],
          extraHeaders: [],
          requiredBaseHeaders: [],
          offerTypeHeadersPresent: [],
          additionalFieldsExpected: []
        });
        setShowPreviewModal(true);
        setPreviewChecked(true);
        showToast('success', 'Preview Ready', `${products.rows.length} products, ${variants.rows.length} variants, ${accessories.rows.length} accessories.`);
        return;
      }

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      
      if (jsonData.length < 2) {
        throw new Error('Excel file is empty or has no data rows');
      }
      
      const headers = jsonData[0].map(h => String(h || '').trim());
      const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== '' && cell !== null && cell !== undefined));
      
      // Define required base headers
      const requiredBaseHeaders = ['SKU Code', 'SKU Description', 'HSN Code', 'GST Rate', 'MRP'];
      const offerTypeHeaders = brandOfferTypes.length > 0 ? brandOfferTypes.map(ot => ot.key.toUpperCase()) : ['CLP', 'SDP', 'NRP', 'KAP', 'HVR', 'SPL'];
      
      // Check for missing required headers
      const missingHeaders = requiredBaseHeaders.filter(h => !headers.includes(h));
      if (missingHeaders.length > 0) {
        setPreviewErrors([`Missing required columns: ${missingHeaders.join(', ')}`]);
      } else {
        setPreviewErrors([]);
      }
      
      // Get brand's additional fields for validation
      let brandAdditionalFields = [];
      try {
        const token = localStorage.getItem('token');
        const brandRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/brands/${importForm.brandId.value}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (brandRes.ok) {
          const brandData = await brandRes.json();
          if (brandData?.additionalFields) {
            // Only the fields applicable to the import category.
            const cid = importForm.categoryId?.value;
            brandAdditionalFields = brandData.additionalFields
              .filter(f => !(f.categories && f.categories.length) || (cid && f.categories.map(String).includes(String(cid))))
              .map(f => f.fieldName);
          }
        }
      } catch (e) {
        console.warn('Could not fetch brand additional fields for preview:', e);
      }
      
      // Validate additional field headers
      const expectedAdditionalHeaders = brandAdditionalFields;
      const missingAdditionalHeaders = expectedAdditionalHeaders.filter(h => !headers.includes(h));
      // Columns recognised by the importer but not required.
      const optionalHeaders = ['Row Type', 'Sub-item', 'Variant', 'Variant ID', 'Preferred Sub-item', 'Sub Qty', 'Sub Items', 'Image', 'Active', 'Series', 'Category'];
      const extraHeaders = headers.filter(h => 
        !requiredBaseHeaders.includes(h) && 
        !offerTypeHeaders.includes(h) && 
        !expectedAdditionalHeaders.includes(h) &&
        !optionalHeaders.includes(h)
      );
      
      // Preview first 5 rows
      const previewRows = rows.slice(0, 5).map((row, idx) => {
        const rowData = {};
        headers.forEach((header, colIdx) => {
          rowData[header] = row[colIdx];
        });
        return { rowNum: idx + 2, data: rowData };
      });

      // Grouped preview: each Item row with its Sub-item / Variant rows nested.
      const rowToObj = (row) => {
        const o = {};
        headers.forEach((h, ci) => { o[h] = row[ci]; });
        return o;
      };
      const rowTypeIdx = headers.findIndex(h => h.toLowerCase() === 'row type');
      const groups = [];
      let currentGroup = null;
      rows.slice(0, 100).forEach((row, idx) => {
        const type = rowTypeIdx >= 0 ? String(row[rowTypeIdx] || '').trim().toLowerCase() : '';
        const isSub = type.startsWith('sub') || type.startsWith('add');
        const isVar = type.startsWith('var');
        const node = { rowNum: idx + 2, kind: isSub ? 'sub' : isVar ? 'variant' : 'item', data: rowToObj(row) };
        if (node.kind === 'item' || !currentGroup) {
          currentGroup = { ...node, children: [] };
          groups.push(currentGroup);
        } else {
          currentGroup.children.push(node);
        }
      });
      
      setPreviewData({
        drawingNotice,
        headers,
        totalRows: rows.length,
        previewRows,
        groups,
        missingHeaders,
        missingAdditionalHeaders,
        extraHeaders,
        requiredBaseHeaders,
        offerTypeHeadersPresent: offerTypeHeaders.filter(h => headers.includes(h)),
        additionalFieldsExpected: expectedAdditionalHeaders
      });
      setShowPreviewModal(true);
      setPreviewChecked(true);
      
      showToast('success', 'Preview Ready', `Found ${rows.length} data rows with ${headers.length} columns.`);
      
    } catch (error) {
      console.error('Preview error:', error);
      showToast('error', 'Preview Failed', error.message || 'Failed to read Excel file.');
      setPreviewErrors([error.message || 'Unknown error']);
      setPreviewData(null);
    }
  };

  const downloadTemplate = async (brandId = null, categoryId = null) => {
    try {
      showToast('info', 'Preparing Download', 'Generating Excel template file...');
      
      // Build URL with brand/category parameters if provided
      let url = `${process.env.REACT_APP_API_BASE_URL}/reports/items/import-template`;
      const params = [];
      if (brandId) params.push(`brandId=${encodeURIComponent(brandId)}`);
      if (categoryId) params.push(`categoryId=${encodeURIComponent(categoryId)}`);
      if (params.length) url += `?${params.join('&')}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
      
      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || `Server error: ${response.status} ${response.statusText}`;
        } catch {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        
        if (response.status === 404) {
          throw new Error('Template generation service is not available. Please contact support.');
        } else if (response.status >= 500) {
          throw new Error('Server is experiencing issues. Please try again in a few moments.');
        } else {
          throw new Error(errorMessage);
        }
      }
      
      // Check if response is actually an Excel file
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('spreadsheetml')) {
        throw new Error('Invalid file format received from server. Expected Excel file but got different format.');
      }
      
      const blob = await response.blob();
      
      // Verify blob size
      if (blob.size === 0) {
        throw new Error('Empty file received from server. Template generation may have failed.');
      }
      
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = brandId ? 'price-list-template-with-additional-fields.xlsx' : 'price-list-template.xlsx';
      a.style.display = 'none';
      
      document.body.appendChild(a);
      a.click();
      
      let successMessage = 'Excel template has been downloaded successfully. Use this template to format your data for import.';
      let detailsMessage = 'Important: Do not modify the column headers (Row 1) in the template. The headers must match exactly: SKU Code, SKU Description, HSN Code, GST Rate, MRP, CLP, SDP, NRP, KAP, HVR, SPL.';
      
      if (brandId) {
        // Fetch brand offer types for message
        let offerTypeNames = 'CLP, SDP, NRP, KAP, HVR, SPL';
        try {
          const token = localStorage.getItem('token');
          const brandRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/brands/${brandId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (brandRes.ok) {
            const brandData = await brandRes.json();
            if (brandData?.offerTypes) {
              const activeTypes = brandData.offerTypes.filter(ot => ot.active !== false);
              if (activeTypes.length > 0) {
                offerTypeNames = activeTypes.map(ot => ot.label || ot.key.toUpperCase()).join(', ');
              }
            }
          }
        } catch (e) {
          console.warn('Could not fetch brand offer types for message:', e);
        }
        
        successMessage = 'Excel template with brand-specific additional fields has been downloaded successfully.';
        detailsMessage = `Important: This template includes additional fields specific to the selected brand. Do not modify any column headers (Row 1) in the template. All headers must match exactly for successful import. Brand offer types: ${offerTypeNames}.`;
      }
      
      showToast('success', 'Template Downloaded', successMessage, detailsMessage);
      
      // Clean up with delay
      setTimeout(() => {
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
        window.URL.revokeObjectURL(blobUrl);
      }, 20000);
      
    } catch (error) {
      console.error('Error downloading template:', error);
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showToast('error', 'Network Error', 
          'Unable to connect to the server to download template.', 
          'Please check your internet connection and try again.');
      } else {
        showToast('error', 'Download Failed', 
          error.message || 'Failed to download Excel template.', 
          'Please try again. If the problem persists, contact support for assistance.');
      }
    }
  };

  // Function to handle basic template download
  const handleBasicTemplateDownload = () => {
    downloadTemplate();
  };

  // Export a brand's items. format: 'full' (current, all columns) or 'import'
  // (exactly the import/template columns, for editing prices and re-importing).
  const exportPriceList = async (brandId, categoryId, format = 'full') => {
    if (!brandId) return;
    try {
      showToast('info', 'Preparing Download', 'Generating pricelist export...');
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ brand: brandId });
      if (categoryId) params.set('categoryId', categoryId);
      if (format === 'import') params.set('format', 'import');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/items/export-excel?${params.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!response.ok) {
        let msg = `Export failed (${response.status})`;
        try { const d = await response.json(); msg = d.error || d.message || msg; } catch {}
        throw new Error(msg);
      }
      const blob = await response.blob();
      if (!blob.size) throw new Error('Empty file received.');
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${format === 'import' ? 'pricelist-import-format' : 'pricelist-export'}-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { if (document.body.contains(a)) document.body.removeChild(a); window.URL.revokeObjectURL(blobUrl); }, 20000);
      showToast('success', 'Export Downloaded',
        format === 'import'
          ? 'Items exported in import format. Edit prices and re-import this file.'
          : 'Items exported. You can re-import this file.');
    } catch (error) {
      console.error('Error exporting pricelist:', error);
      showToast('error', 'Export Failed', error.message || 'Failed to export items.');
    }
  };

  // Header "Export Price List": use the brand filter if set, else ask for one.
  const handleExportPriceList = () => {
    if (filters.brand) {
      exportPriceList(filters.brand, filters.category, 'full');
    } else {
      setBrandModalMode('export');
      setTemplateBrand(null);
      setTemplateCategory(null);
      setTemplateCategoryOptions([]);
      setShowBrandSelection(true);
    }
  };

  // Header "Export for Import": export in the import/template format.
  const handleExportImportFormat = () => {
    if (filters.brand) {
      exportPriceList(filters.brand, filters.category, 'import');
    } else {
      setBrandModalMode('exportImport');
      setTemplateBrand(null);
      setTemplateCategory(null);
      setTemplateCategoryOptions([]);
      setShowBrandSelection(true);
    }
  };

  // Function to show brand selection modal
  const showBrandSelectionModal = () => {
    setBrandModalMode('template');
    setTemplateCategory(null);
    setTemplateCategoryOptions([]);
    setShowBrandSelection(true);
  };

  // When a brand is chosen for the template, load its categories so the user
  // can optionally scope the template to a category.
  const handleTemplateBrandChange = async (selected) => {
    setTemplateBrand(selected || null);
    setTemplateCategory(null);
    setTemplateCategoryOptions([]);
    if (!selected?.value) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/brands/${selected.value}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) return;
      const brand = await res.json();
      const cats = Array.isArray(brand.categories) ? brand.categories : [];
      setTemplateCategoryOptions(cats.map(c => ({ value: c._id, label: c.name })));
    } catch (e) {
      console.warn('Could not load brand categories for template:', e);
    }
  };

  // Function to cancel brand selection
  const cancelBrandSelection = () => {
    setShowBrandSelection(false);
    setTemplateBrand(null);
    setTemplateCategory(null);
    setTemplateCategoryOptions([]);
  };

  // Confirm button of the brand-selection modal (template or export).
  const handleBrandModalConfirm = () => {
    if (!templateBrand) {
      showToast('warning', 'Brand Selection Required', 'Please select a brand.');
      return;
    }
    const brandId = templateBrand.value;
    const categoryId = templateCategory?.value || null;
    setShowBrandSelection(false);
    setTemplateBrand(null);
    setTemplateCategory(null);
    setTemplateCategoryOptions([]);
    if (brandModalMode === 'template') {
      downloadTemplate(brandId, categoryId);
    } else {
      exportPriceList(brandId, categoryId, brandModalMode === 'exportImport' ? 'import' : 'full');
    }
  };

  // Enable/disable an item.
  const toggleItemActive = async (item) => {
    const nextActive = item.isActive === false;
    setActiveTogglingId(item._id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/items/${item._id}/active`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ isActive: nextActive })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || data.details || 'Failed to update status');
      // Optimistically update the row.
      setItems(prev => prev.map(it => it._id === item._id ? { ...it, isActive: data.isActive !== undefined ? data.isActive : nextActive } : it));
      showToast('success', 'Status Updated', `Item ${data.isActive === false ? 'marked inactive' : 'activated'}.`);
    } catch (error) {
      console.error('Error toggling item status:', error);
      showToast('error', 'Update Failed', error.message || 'Could not update item status.');
    } finally {
      setActiveTogglingId(null);
    }
  };

  // Load latest item with populated audit trail and open the modal
  const showItemDetails = async (item) => {
    try {
      // Reset expanded updates state when opening modal
      setExpandedUpdates({});
      
      // Prefer stable _id based fetch to avoid SKU mismatches
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/items/id/${encodeURIComponent(item._id)}`);
      if (response.ok) {
        const data = await response.json();
        setModalItem(data);
        setModalOpen(true);
      } else {
        throw new Error('Failed to fetch item details');
      }
    } catch (err) {
      // Fallback: try by skuCode once
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/items/${encodeURIComponent(item.skuCode)}`);
        if (response.ok) {
          const data = await response.json();
          setModalItem(data);
        } else {
          setModalItem(item);
        }
      } catch (_) {
        setModalItem(item);
      }
      setModalOpen(true);
    }
  };

  // Modal component for item details
  const renderItemDetailsModal = () => {
    if (!modalOpen || !modalItem) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">{modalItem.skuDescription}</h2>
                <p className="text-blue-100 mt-1">SKU: {modalItem.skuCode}</p>
              </div>
              <button
                onClick={closeModal}
                className="text-blue-100 hover:text-white p-2 rounded-lg hover:bg-blue-800 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className={`bg-gradient-to-r rounded-xl p-4 border ${darkMode ? 'from-gray-700 to-gray-800 border-gray-600' : 'from-gray-50 to-slate-50 border-gray-200'}`}>
                <h4 className={`text-base font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'} mb-3`}>Basic Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Brand</p>
                    <p className="text-sm font-semibold text-blue-600">{modalItem.brand?.name || 'N/A'}</p>
                  </div>
                  <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Category</p>
                    <p className="text-sm font-semibold text-purple-600">{modalItem.category?.name || 'N/A'}</p>
                  </div>
                  <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Series</p>
                    <p className="text-sm font-semibold text-orange-600">{modalItem.series}</p>
                  </div>
                  <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>HSN Code</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{modalItem.hsnCode}</p>
                  </div>
                  <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>GST Rate</p>
                    <p className="text-sm font-semibold text-red-600">{modalItem.gstRate}%</p>
                  </div>
                  <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>MRP</p>
                    <p className="text-sm font-semibold text-green-600">₹{modalItem.mrp?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Offer Types */}
              <div className={`bg-gradient-to-r rounded-xl p-4 border ${darkMode ? 'from-gray-700 to-gray-800 border-gray-600' : 'from-gray-50 to-slate-50 border-gray-200'}`}>
                <h4 className={`text-base font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'} mb-3`}>Offer Types</h4>
                <div className="grid grid-cols-2 gap-3">
                  {(modalItem.brand?.offerTypes || []).filter(ot => ot.active !== false).map(ot => {
                    const key = ot.key.toLowerCase();
                    const value = modalItem.offerType?.[key];
                    return isValidValue(value) && (
                      <div key={ot.key} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                        <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{ot.label || ot.key.toUpperCase()}</p>
                        <p className="text-sm font-semibold text-blue-600">₹{value?.toLocaleString()}</p>
                      </div>
                    );
                  })}
                  {/* Fallback for items without brand offerTypes */}
                  {!modalItem.brand?.offerTypes && Object.keys(modalItem.offerType || {}).map(key => 
                    isValidValue(modalItem.offerType[key]) && (
                      <div key={key} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                        <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{key.toUpperCase()}</p>
                        <p className="text-sm font-semibold text-blue-600">₹{modalItem.offerType[key]?.toLocaleString()}</p>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Additional Fields */}
              {modalItem.brand?.additionalFields?.length > 0 && modalItem.additionalFieldValues && Object.keys(modalItem.additionalFieldValues).length > 0 && (
                <div className={`lg:col-span-2 bg-gradient-to-r rounded-xl p-4 border ${darkMode ? 'from-gray-700 to-gray-800 border-gray-600' : 'from-gray-50 to-slate-50 border-gray-200'}`}>
                  <h4 className={`text-base font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'} mb-3`}>
                    Additional Fields
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {modalItem.brand.additionalFields
                      .filter((f) => !(f.categories && f.categories.length) ||
                        (modalItem.category && f.categories.map(String).includes(String(modalItem.category._id || modalItem.category))))
                      .map((field) => {
                      const val = modalItem.additionalFieldValues[field.fieldName];
                      if (val === undefined || val === null || val === '') return null;
                      return (
                        <div key={field.fieldName} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                          <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{field.fieldName}</p>
                          <p className={`text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{val}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Audit Information */}
              {(modalItem.createdBy || modalItem.updatedBy || modalItem.createdAt || modalItem.lastUpdatedAt) && (
                <div className={`lg:col-span-2 bg-gradient-to-r rounded-xl p-4 border ${darkMode ? 'from-gray-700 to-gray-800 border-gray-600' : 'from-gray-50 to-slate-50 border-gray-200'}`}>
                  <h4 className={`text-base font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'} mb-3 flex items-center`}>
                    <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Audit Trail
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    {modalItem.createdBy && (
                      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                        <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Created By</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{modalItem.createdBy?.name || modalItem.createdBy?.email || 'N/A'}</p>
                      </div>
                    )}
                    {modalItem.createdAt && (
                      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                        <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Created At</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{new Date(modalItem.createdAt).toLocaleString()}</p>
                      </div>
                    )}
                    {modalItem.lastUpdatedAt && (
                      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                        <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Last Updated At</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{new Date(modalItem.lastUpdatedAt).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                  {Array.isArray(modalItem.updatedBy) && modalItem.updatedBy.length > 0 && (
                    <div className="mt-4">
                      <div className={`text-xs font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-2`}>Update History</div>
                      <div className="space-y-2 max-h-56 overflow-auto pr-1">
                        {modalItem.updatedBy
                          .slice()
                          .sort((a,b) => new Date(b.date) - new Date(a.date))
                          .map((log, idx) => {
                            const updateId = `${modalItem._id}-${idx}`;
                            return (
                              <div key={idx} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-3 shadow-sm border`}>
                                <div className="flex items-center justify-between">
                                  <div className={`text-xs ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                    <span className="font-medium">{
                                      log.employee?.name ||
                                      log.employee?.email ||
                                      (typeof log.employee === 'string' ? log.employee : (log.employee?._id || 'Unknown'))
                                    }</span>
                                    <span className="text-gray-400"> • </span>
                                    <span>{new Date(log.date).toLocaleString()}</span>
                                  </div>
                                </div>
                                {log.pastData && (
                                  <div className={`mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px] ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {log.pastData.mrp !== undefined && (
                                      <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border rounded p-2`}>
                                        <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Previous MRP</div>
                                        <div>₹{log.pastData.mrp}</div>
                                      </div>
                                    )}
{log.pastData.gstRate !== undefined && (
                                        <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border rounded p-2`}>
                                          <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Previous GST</div>
                                          <div>{log.pastData.gstRate}%</div>
                                        </div>
                                      )}
                                      {log.pastData.offerType && Object.entries(log.pastData.offerType).map(([key, value]) => (
                                        value !== undefined && (
                                          <div key={key} className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border rounded p-2`}>
                                            <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Prev {key.toUpperCase()}</div>
                                            <div>₹{value}</div>
                                          </div>
                                        )
                                      ))}
                                    </div>
                                )}
                                {log.pastData && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => toggleUpdateDetails(updateId)}
                                      className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer focus:outline-none"
                                    >
                                      {expandedUpdates[updateId] ? 'Hide Data' : 'View Data'}
                                    </button>
                                    {expandedUpdates[updateId] && (
                                      <div className={`mt-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border rounded p-2 text-[11px] ${darkMode ? 'text-gray-200' : 'text-gray-700'} overflow-auto max-h-48`}>
                                        {(() => {
                                          try {
                                            const { image, updatedBy: _logs, __v, ...rest } = log.pastData || {};
                                            return (
                                              <pre className="whitespace-pre-wrap break-words">{JSON.stringify(rest, null, 2)}</pre>
                                            );
                                          } catch (e) {
                                            return <div className={`${darkMode ? 'text-red-400' : 'text-red-600'}`}>Unable to display snapshot</div>;
                                          }
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} px-6 py-4 border-t`}>
            <div className="flex justify-between items-center">
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Item ID: {modalItem._id}
              </div>
              <button
                onClick={closeModal}
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Toast notification component
  const renderToast = () => {
    if (!toast.show) return null;

    const getToastIcon = () => {
      switch (toast.type) {
        case 'success':
          return <FaCheckCircle className="text-green-500 text-xl" />;
        case 'error':
          return <FaExclamationCircle className="text-red-500 text-xl" />;
        case 'warning':
          return <FaExclamationTriangle className="text-yellow-500 text-xl" />;
        case 'info':
        default:
          return <FaInfoCircle className="text-blue-500 text-xl" />;
      }
    };

    const getToastColors = () => {
      switch (toast.type) {
        case 'success':
          return 'bg-green-50 border-green-200 text-green-800';
        case 'error':
          return 'bg-red-50 border-red-200 text-red-800';
        case 'warning':
          return 'bg-yellow-50 border-yellow-200 text-yellow-800';
        case 'info':
        default:
          return 'bg-blue-50 border-blue-200 text-blue-800';
      }
    };

    return (
      <div className="fixed top-4 right-4 z-50 max-w-lg w-full">
        <div className={`${getToastColors()} border rounded-lg p-4 shadow-lg`}>
          <div className="flex items-start">
            <div className="flex-shrink-0 mr-3 mt-0.5">
              {getToastIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-sm mb-1 break-words">{toast.title}</h4>
              <p className="text-sm mb-2 break-words">{toast.message}</p>
              {toast.details && (
                <div className={`text-xs opacity-90 ${darkMode ? 'bg-gray-800' : 'bg-white'} bg-opacity-50 rounded p-3 mt-2 whitespace-pre-line max-h-32 overflow-y-auto border`}>
                  <div className="font-mono text-xs leading-relaxed">
                    {toast.details}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={hideToast}
              className="flex-shrink-0 ml-2 p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderImportModal = () => {
    if (!showImportModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto`}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Import Price List</h2>
                <p className="text-blue-100 mt-1">Upload and process Excel files with item pricing data</p>
              </div>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportForm({ file: null, brandId: null, categoryId: null, employeeId: null, series: seriesOptions[0], applyGstToOffers: false, gstOfferTypes: ['clp','sdp','nrp','kap','hvr','spl'] });
                  setImportResults(null);
                  setPreviewChecked(false);
                  setPreviewData(null);
                  setPreviewErrors([]);
                }}
                className="text-blue-100 hover:text-white p-2 rounded-lg hover:bg-blue-800 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {importResults ? (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <FaCheckCircle className="text-green-600 text-2xl mr-3" />
                    <h3 className="font-bold text-green-800 text-lg">Import Completed Successfully</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 border border-green-100`}>
                      <div className={`text-2xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{importResults.total}</div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Records</div>
                    </div>
                    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 border border-green-100`}>
                      <div className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{importResults.successful}</div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>New Items Created</div>
                    </div>
                    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 border border-green-100`}>
                      <div className="text-2xl font-bold text-blue-600">{importResults.updated}</div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Items Updated</div>
                    </div>
                    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 border border-green-100`}>
                      <div className="text-2xl font-bold text-red-600">{importResults.failed}</div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Failed Records</div>
                    </div>
                  </div>
                </div>

                {importResults.errors.length > 0 && (
                  <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <FaExclamationTriangle className="text-red-600 text-xl mr-3" />
                      <h4 className="font-bold text-red-800">Import Errors ({importResults.errors.length})</h4>
                      <button
                        onClick={() => {
                          const rows = [['Message'], ...(importResults.errors || []).map(e => [e && e.message ? e.message : String(e)])];
                          const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
                          const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
                          const a = document.createElement('a');
                          a.href = url; a.download = 'import-errors.csv'; a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="ml-auto text-xs px-3 py-1 rounded border border-red-300 text-red-700 hover:bg-red-100"
                      >
                        Download errors (CSV)
                      </button>
                    </div>
                    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-4 max-h-48 overflow-y-auto border border-red-100`}>
                      {importResults.errors.map((error, index) => (
                        <div key={index} className={`text-sm ${darkMode ? 'text-red-300 bg-red-900/20' : 'text-red-700 bg-red-50'} mb-2 p-2 rounded border-l-4 border-red-400`}>
                          {error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {importResults.warnings && importResults.warnings.length > 0 && (
                  <div className={`border border-amber-200 rounded-xl p-6 ${darkMode ? 'bg-amber-900/20' : 'bg-amber-50'}`}>
                    <h4 className={`font-bold mb-3 ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>Warnings ({importResults.warnings.length})</h4>
                    <div className={`rounded-lg p-4 max-h-48 overflow-y-auto border border-amber-100 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                      {importResults.warnings.slice(0, 10).map((w, i) => (
                        <div key={i} className={`text-sm mb-2 ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>{w && w.message ? w.message : String(w)}</div>
                      ))}
                      {importResults.warnings.length > 10 && (
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>... and {importResults.warnings.length - 10} more warnings</p>
                      )}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportForm({ file: null, brandId: null, categoryId: null, employeeId: null, series: seriesOptions[0], applyGstToOffers: false, gstOfferTypes: ['clp','sdp','nrp','kap','hvr','spl'] });
                    setImportResults(null);
                    setPreviewChecked(false);
                    setPreviewData(null);
                    setPreviewErrors([]);
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 font-semibold transition-all duration-200"
                >
                  Close & Refresh
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className={`border-l-4 border-blue-500 ${darkMode ? 'bg-blue-900/20' : 'bg-blue-50'} p-4 rounded-r-lg`}>
                  <div className="flex items-center">
                    <FaDownload className="text-blue-600 mr-3" />
                    <div>
                      <h4 className={`font-semibold ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>Download Template First</h4>
                      <p className={`${darkMode ? 'text-blue-300' : 'text-blue-700'} text-sm`}>Three simple tabs: Products, Variants, Accessories (with example rows on separate Example tabs).</p>
                      <p className={`${darkMode ? 'text-blue-300' : 'text-blue-600'} text-xs mt-1 font-medium`}>⚠️ Do not modify column headers (Row 1) in the template!</p>
                    </div>
                    <div className="ml-auto flex gap-2">
                      <button
                        onClick={handleBasicTemplateDownload}
                        className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
                      >
                        <FaDownload /> Basic Template
                      </button>
                      <button
                        onClick={showBrandSelectionModal}
                        className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 flex items-center gap-2 transition-colors"
                      >
                        <FaBox /> With Additional Fields
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Brand Selection *
                    </label>
                    <Select
                      value={importForm.brandId}
                      onChange={(selectedOption) => {
                        setImportForm(prev => ({ ...prev, brandId: selectedOption, categoryId: null }));
                        setPreviewChecked(false);
                        if (selectedOption?.value) {
                          fetchBrandOfferTypes(selectedOption.value);
                        } else {
                          setBrandOfferTypes([]);
                        }
                      }}
                      options={brands}
                      styles={darkMode ? darkSelectStyles : selectStyles}
                      placeholder="Choose a brand..."
                      isSearchable
                      isClearable
                      noOptionsMessage={() => "No brands found"}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Category Selection *
                    </label>
                    <Select
                      value={importForm.categoryId}
                      onChange={(selectedOption) => setImportForm(prev => ({ ...prev, categoryId: selectedOption }))}
                      options={importForm.brandId
                        ? (brands.find(b => b.value === importForm.brandId.value)?.categories || [])
                        : categories}
                      styles={darkMode ? darkSelectStyles : selectStyles}
                      placeholder="Choose a category..."
                      isSearchable
                      isClearable
                      noOptionsMessage={() => "No categories found"}
                    />
                    {brands.find(b => b.value === importForm.brandId?.value)?.advancedItems && (
                      <p className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Default category - rows with their own Category (advanced brands) override it.
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Employee Selection *
                  </label>
                  <Select
                    value={importForm.employeeId}
                    onChange={(selectedOption) => setImportForm(prev => ({ ...prev, employeeId: selectedOption }))}
                    options={employees}
                    styles={darkMode ? darkSelectStyles : selectStyles}
                    placeholder="Choose an employee..."
                    isSearchable
                    isClearable
                    noOptionsMessage={() => "No employees found"}
                  />
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-2`}>Select the employee who is performing this import operation.</p>
                </div>

                <div>
                  <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Series
                  </label>
                  <Select
                    value={importForm.series}
                    onChange={(selectedOption) => setImportForm(prev => ({ ...prev, series: selectedOption }))}
                    options={seriesOptions}
                    styles={darkMode ? darkSelectStyles : selectStyles}
                    placeholder="Choose a series..."
                    isSearchable={false}
                    noOptionsMessage={() => "No series found"}
                  />
                </div>

                <div className={`flex items-start gap-3 p-4 border rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <input
                    id="apply-gst-offers"
                    type="checkbox"
                    checked={importForm.applyGstToOffers}
                    onChange={(e) => setImportForm(prev => ({ ...prev, applyGstToOffers: e.target.checked }))}
                    className={`mt-1 h-5 w-5 text-blue-600 ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded focus:ring-blue-500`}
                  />
                  <label htmlFor="apply-gst-offers" className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    <span className="font-semibold">Add GST to offer prices on import</span>
                    <span className={`block text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                      When enabled, offer type values ({brandOfferTypes.map(ot => ot.label).join(', ') || 'brand offer types'}) will be increased by the item's GST rate (%). MRP remains unchanged.
                    </span>
                  </label>
                </div>

                {importForm.applyGstToOffers && (
                  <div className={`p-4 border rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-3`}>Apply GST to selected offer types</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {(brandOfferTypes.length > 0 ? brandOfferTypes.map(ot => ({
                        key: ot.key,
                        label: ot.label
                      })) : [
                        { key: 'clp', label: 'CLP' },
                        { key: 'sdp', label: 'SDP' },
                        { key: 'nrp', label: 'NRP' },
                        { key: 'kap', label: 'KAP' },
                        { key: 'hvr', label: 'HVR' },
                        { key: 'spl', label: 'SPL' },
                      ]).map(opt => (
                        <label key={opt.key} className={`inline-flex items-center gap-2 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          <input
                            type="checkbox"
                            checked={(importForm.gstOfferTypes || []).includes(opt.key)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setImportForm(prev => {
                                const set = new Set(prev.gstOfferTypes);
                                if (checked) set.add(opt.key); else set.delete(opt.key);
                                return { ...prev, gstOfferTypes: Array.from(set) };
                              });
                            }}
                            className={`h-4 w-4 text-blue-600 ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded focus:ring-blue-500`}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-2`}>Tip: Leave all selected to apply GST to every offer type.</p>
                  </div>
                )}

                <div>
                  <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Excel File Upload *
                  </label>
                  <div className={`border-2 border-dashed ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg p-6 text-center hover:border-blue-400 transition-colors`}>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                      required
                    />
                    <div className="space-y-3">
                      <FaUpload className="mx-auto text-3xl text-gray-400" />
                      <div>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-blue-600 hover:text-blue-700 font-semibold"
                        >
                          Click to upload
                        </button>
                        <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}> or drag and drop</span>
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Excel files only (.xlsx, .xls)</p>
                    </div>
                    {importForm.file && (
                      <div className={`mt-4 p-3 ${darkMode ? 'bg-green-900/20 border-green-700 text-green-300' : 'bg-green-50 border-green-200 text-green-700'} border rounded-lg`}>
                        <div className="flex items-center justify-center">
                          <FaCheckCircle className={`${darkMode ? 'text-green-400' : 'text-green-600'} mr-2`} />
                          <span className={`${darkMode ? 'text-green-300' : 'text-green-700'} font-medium`}>{importForm.file.name}</span>
                        </div>
                        <div className="mt-3 flex gap-2 justify-center">
                          <button
                            type="button"
                            onClick={previewExcelFile}
                            disabled={!importForm.brandId || !importForm.brandId.value}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 transition-colors text-sm"
                          >
                            <FaSearch /> Check Excel
                          </button>
                        </div>
                        {!importForm.brandId && (
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-2 text-center`}>Select a brand to enable Excel validation</p>
                        )}
                        {previewChecked && (
                          <p className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-600'} mt-2 text-center`}>✓ Excel validated - Ready for import</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <label className={`flex items-center gap-2 text-sm cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-200'}`}>
                  <input
                    type="checkbox"
                    checked={strictImport}
                    onChange={(e) => setStrictImport(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Strict mode (reject the whole file if any row has an error)
                </label>
                <div className={`flex gap-4 pt-6 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <button
                    onClick={handleImport}
                    disabled={importLoading || !importForm.file || !importForm.brandId || !importForm.categoryId || !importForm.employeeId || !importForm.series || !previewChecked}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-semibold transition-all duration-200"
                  >
                    {importLoading ? (
                      <>
                        <FaSpinner className="animate-spin text-lg" /> Processing Import...
                      </>
                    ) : (
                      <>
                        <FaUpload className="text-lg" /> Start Import
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowImportModal(false);
                      setImportForm({ file: null, brandId: null, categoryId: null, employeeId: null, series: seriesOptions[0], applyGstToOffers: false, gstOfferTypes: ['clp','sdp','nrp','kap','hvr','spl'] });
                      setImportResults(null);
                      setPreviewChecked(false);
                      setPreviewData(null);
                      setPreviewErrors([]);
                    }}
                    className={`px-6 py-3 border-2 ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'} rounded-lg font-semibold transition-colors`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Brand Selection Modal for Template Download
  const renderBrandSelectionModal = () => {
    if (!showBrandSelection) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl`}>
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaBox className="text-2xl text-green-600" />
            </div>
            <h3 className={`text-xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-2`}>
              {brandModalMode === 'template' ? 'Select Brand for Template'
                : brandModalMode === 'exportImport' ? 'Select Brand to Export (Import Format)'
                : 'Select Brand to Export'}
            </h3>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>
              {brandModalMode === 'template'
                ? 'Choose a brand to generate a template with its specific additional fields.'
                : brandModalMode === 'exportImport'
                  ? 'Export items in the import format so you can edit prices and re-import the file.'
                  : 'Choose the brand whose items you want to export in pricelist format.'}
            </p>
          </div>

          <div className="mb-6">
            <label className={`block text-sm font-bold ${darkMode ? 'text-gray-100' : 'text-gray-700'} mb-3`}>
              Brand Selection *
            </label>
            <Select
              value={templateBrand}
              onChange={handleTemplateBrandChange}
              options={brands}
              styles={darkMode ? darkSelectStyles : selectStyles}
              placeholder="Choose a brand..."
              isSearchable
              isClearable
              noOptionsMessage={() => "No brands found"}
            />
          </div>

          {templateCategoryOptions.length > 0 && (
            <div className="mb-6">
              <label className={`block text-sm font-bold ${darkMode ? 'text-gray-100' : 'text-gray-700'} mb-3`}>
                Category (optional)
              </label>
              <Select
                value={templateCategory}
                onChange={(opt) => setTemplateCategory(opt || null)}
                options={templateCategoryOptions}
                styles={darkMode ? darkSelectStyles : selectStyles}
                placeholder="All categories"
                isSearchable
                isClearable
                noOptionsMessage={() => "No categories"}
              />
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Select a category to include only its additional fields in the template.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={cancelBrandSelection}
              className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBrandModalConfirm}
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              {brandModalMode === 'template' ? (<><FaDownload /> Download Template</>)
                : brandModalMode === 'exportImport' ? (<><FaDownload /> Export for Import</>)
                : (<><FaDownload /> Export Price List</>)}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Preview Modal Component
  const renderPreviewModal = () => {
    if (!showPreviewModal || !previewData) return null;

    const { headers, totalRows, previewRows, missingHeaders, missingAdditionalHeaders, extraHeaders, requiredBaseHeaders, offerTypeHeadersPresent, additionalFieldsExpected, drawingNotice } = previewData;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden`}>
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Excel Preview & Validation</h2>
                <p className="text-blue-100 mt-1">{totalRows} rows • {headers.length} columns</p>
              </div>
              <button
                onClick={() => { setShowPreviewModal(false); setPreviewData(null); setPreviewErrors([]); }}
                className="text-blue-100 hover:text-white p-2 rounded-lg hover:bg-blue-800 transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {drawingNotice && (
              <div className={`mb-4 border rounded-xl p-3 text-sm ${darkMode ? 'bg-yellow-900/20 border-yellow-700 text-yellow-200' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>
                <span className="font-semibold">Image drawings: </span>{drawingNotice}
              </div>
            )}
            {/* Validation Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className={`${darkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4`}>
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <FaSearch className="text-blue-600 text-xl" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-600 font-medium">Total Rows</p>
                    <p className="text-2xl font-bold text-blue-900">{totalRows}</p>
                  </div>
                </div>
              </div>

              <div className={`${darkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'} border rounded-xl p-4`}>
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-full">
                    <FaCheckCircle className="text-green-600 text-xl" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-600 font-medium">Required Columns</p>
                    <p className="text-2xl font-bold text-green-900">
                      {requiredBaseHeaders.filter(h => headers.includes(h)).length} / {requiredBaseHeaders.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`${darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'} border rounded-xl p-4`}>
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-full">
                    <FaBox className="text-purple-600 text-xl" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-purple-600 font-medium">Additional Fields</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {additionalFieldsExpected.filter(h => headers.includes(h)).length} / {additionalFieldsExpected.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className={missingHeaders.length > 0 || missingAdditionalHeaders.length > 0 ? `${darkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'} border rounded-xl p-4` : `${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border rounded-xl p-4`}>
                <div className="flex items-center">
                  <div className={`p-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-full`}>
                    <FaExclamationTriangle className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} text-xl`} />
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} font-medium`}>Issues Found</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {missingHeaders.length + missingAdditionalHeaders.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Errors/Warnings */}
            {(missingHeaders.length > 0 || missingAdditionalHeaders.length > 0) && (
              <div className={`mb-6 p-4 ${darkMode ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'} border rounded-xl`}>
                <h4 className="font-bold text-red-800 mb-2 flex items-center gap-2">
                  <FaExclamationTriangle /> Validation Issues
                </h4>
                {missingHeaders.length > 0 && (
                  <div className={`${darkMode ? 'text-red-300' : 'text-red-700'} text-sm mb-2`}>
                    <strong>Missing Required Columns:</strong> {missingHeaders.join(', ')}
                  </div>
                )}
                {missingAdditionalHeaders.length > 0 && (
                  <div className={`${darkMode ? 'text-red-300' : 'text-red-700'} text-sm`}>
                    <strong>Missing Brand Additional Fields:</strong> {missingAdditionalHeaders.join(', ')}
                  </div>
                )}
                {extraHeaders.length > 0 && (
                  <div className={`${darkMode ? 'text-yellow-300' : 'text-yellow-700'} text-sm mt-2`}>
                    <strong>Extra Columns (will be ignored):</strong> {extraHeaders.join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* View toggle */}
            <div className="flex items-center justify-between mb-2">
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {previewViewMode === 'flat'
                  ? `${previewRows.length} of ${totalRows} rows`
                  : `Grouped (${(previewData.groups || []).length} items)`}
              </p>
              <div className={`inline-flex rounded-lg border overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                <button type="button" onClick={() => setPreviewViewMode('flat')} className={`px-3 py-1 text-xs font-semibold ${previewViewMode === 'flat' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700')}`}>Flat</button>
                <button type="button" onClick={() => setPreviewViewMode('grouped')} className={`px-3 py-1 text-xs font-semibold ${previewViewMode === 'grouped' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700')}`}>Grouped</button>
              </div>
            </div>

            {previewViewMode === 'grouped' ? (
              <div className={`rounded-lg border overflow-auto max-h-[50vh] ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className="p-2 space-y-1">
                  {(previewData.groups || []).map(g => (
                    <div key={g.rowNum}>
                      <div className={`px-2 py-1 rounded ${darkMode ? 'bg-gray-700 text-gray-100' : 'bg-gray-100 text-gray-900'}`}>
                        <span className="font-mono text-xs opacity-60 mr-2">Row {g.rowNum}</span>
                        <span className="font-semibold">{g.data['SKU Code'] || '(no SKU)'}</span>
                        <span className="ml-2">{g.data['SKU Description'] || ''}</span>
                        <span className="ml-2 opacity-70">₹{g.data['MRP'] ?? ''}</span>
                      </div>
                      {(g.children || []).map(c => (
                        <div key={c.rowNum} className={`ml-6 px-2 py-0.5 text-xs flex flex-wrap gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <span className={`text-[10px] font-bold uppercase ${c.kind === 'variant' ? 'text-violet-500' : 'text-amber-600'}`}>{c.kind}</span>
                          <span className="font-mono opacity-60">Row {c.rowNum}</span>
                          <span>{c.data['SKU Code'] || ''}</span>
                          <span>{c.kind === 'variant' ? (c.data['Variant'] || '') : (c.data['SKU Description'] || '')}</span>
                          <span className="opacity-70">₹{c.data['MRP'] ?? ''}</span>
                          {c.kind === 'variant' && c.data['Preferred Sub-item'] ? <span className="opacity-70">prefers: {String(c.data['Preferred Sub-item'])}</span> : null}
                          {c.kind === 'sub' && c.data['Sub Qty'] ? <span className="opacity-70">× {c.data['Sub Qty']}</span> : null}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-b`}>
                    <th className={`px-3 py-2 text-left font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} w-12`}>Row</th>
                    {headers.map((header, idx) => (
                      <th key={header} className={`px-3 py-2 text-left font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'} min-w-[120px]`}>
                        <span className={requiredBaseHeaders.includes(header) ? 'text-red-600' : 
                          offerTypeHeadersPresent.includes(header) ? 'text-blue-600' :
                          additionalFieldsExpected.includes(header) ? 'text-purple-600' : `${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                          {header}
                          {requiredBaseHeaders.includes(header) && <span className="text-red-400 ml-1">*</span>}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, rowIdx) => (
                    <tr key={row.rowNum} className={rowIdx % 2 === 0 ? `${darkMode ? 'bg-gray-800' : 'bg-white'}` : `${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <td className={`px-3 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'} font-medium border-r ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>{row.rowNum}</td>
                      {headers.map((header, colIdx) => (
                        <td key={header} className={`px-3 py-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'} max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap`}>
                          {row.data[header] !== undefined && row.data[header] !== '' ? (
                            <span title={String(row.data[header])}>{String(row.data[header]).substring(0, 50)}</span>
                          ) : (
                            <span className="text-gray-300 italic">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {totalRows > previewRows.length && (
                    <tr className={`${darkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                      <td colSpan={headers.length + 1} className="px-3 py-3 text-center text-blue-600 text-sm">
                        ... and {totalRows - previewRows.length} more rows
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            )}

            {/* Column Legend */}
            <div className={`mt-6 p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl`}>
              <h4 className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-3`}>Column Legend</h4>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-red-500"></span>
                  <span className="text-red-600">Required (*)</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-blue-500"></span>
                  <span className="text-blue-600">Offer Type</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-purple-500"></span>
                  <span className="text-purple-600">Additional Field</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className={`w-3 h-3 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></span>
                  <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Other</span>
                </span>
              </div>
            </div>
          </div>

          <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} px-6 py-4 border-t flex justify-end gap-3`}>
            <button
              onClick={() => { setShowPreviewModal(false); setPreviewData(null); setPreviewErrors([]); }}
              className="bg-gray-600 text-white py-2 px-6 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FaTimes className="mr-2" /> Close Preview
            </button>
          </div>
        </div>
      </div>
    );
  };
  const renderProgressModal = () => {
    if (!importProgress.show) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 max-w-lg w-full mx-4 shadow-2xl`}>
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaSpinner className="text-2xl text-blue-600 animate-spin" />
            </div>
            
            <h3 className={`text-xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-2`}>
              Processing Import
            </h3>
            
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
              {importProgress.message}
            </p>

            {/* Progress Bar */}
            <div className={`w-full ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} rounded-full h-3 mb-4`}>
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${importProgress.progress}%` }}
              />
            </div>

            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} space-y-1`}>
              <div className="flex justify-between">
                <span>Progress:</span>
                <span className="font-semibold">{importProgress.progress.toFixed(1)}%</span>
              </div>
              
              {importProgress.processedCount > 0 && (
                <div className="flex justify-between">
                  <span>Processed:</span>
                  <span className="font-semibold">
                    {importProgress.processedCount} / {importProgress.totalCount}
                  </span>
                </div>
              )}
              
              {importProgress.timeElapsed > 0 && (
                <div className="flex justify-between">
                  <span>Time Elapsed:</span>
                  <span className="font-semibold">{importProgress.timeElapsed.toFixed(1)}s</span>
                </div>
              )}
              
              {importProgress.eta > 0 && (
                <div className="flex justify-between">
                  <span>Estimated Time:</span>
                  <span className="font-semibold">{importProgress.eta.toFixed(1)}s remaining</span>
                </div>
              )}
              
              {importProgress.throughput > 0 && (
                <div className="flex justify-between">
                  <span>Throughput:</span>
                  <span className="font-semibold">{importProgress.throughput.toFixed(2)} items/sec</span>
                </div>
              )}
            </div>

            {importProgress.currentItem && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Processing:</span> {importProgress.currentItem}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Results Modal Component
  const renderResultsModal = () => {
    if (!showResultsModal || !detailedResults) return null;

    const stats = detailedResults.processingStatistics || {};
    const breakdown = detailedResults.detailedBreakdown || {};
    const exportData = detailedResults.exportData || {};

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden shadow-2xl`}>
          {/* Header */}
          <div className={`p-6 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'} bg-gradient-to-r from-green-600 to-green-700`}>
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-white">Import Results</h3>
                <p className="text-green-100 mt-1">
                  Detailed breakdown of your import process
                </p>
              </div>
              <button
                onClick={() => setShowResultsModal(false)}
                className="text-green-100 hover:text-white hover:bg-green-600 p-2 rounded-full transition-colors"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <FaBox className="text-blue-600 text-xl" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-600 font-medium">Total Processed</p>
                    <p className="text-2xl font-bold text-blue-900">{stats.totalRows || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-full">
                    <FaCheckCircle className="text-green-600 text-xl" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-600 font-medium">Successful</p>
                    <p className="text-2xl font-bold text-green-900">{stats.successfulUpdates || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="p-3 bg-red-100 rounded-full">
                    <FaExclamationTriangle className="text-red-600 text-xl" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-600 font-medium">Failed</p>
                    <p className="text-2xl font-bold text-red-900">{stats.failedUpdates || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-full">
                    <FaInfoCircle className="text-purple-600 text-xl" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-purple-600 font-medium">Success Rate</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {stats.totalRows > 0 ? ((stats.successfulUpdates / stats.totalRows) * 100).toFixed(1) : 0}%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl p-6 mb-8`}>
              <h4 className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>Performance Metrics</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Processing Time</p>
                  <p className={`text-xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{stats.processingTime || 'N/A'}</p>
                </div>
                <div className="text-center">
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Average per Item</p>
                  <p className={`text-xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{stats.averageTimePerItem || 'N/A'}</p>
                </div>
                <div className="text-center">
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Throughput</p>
                  <p className={`text-xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{stats.throughput || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'} rounded-xl p-6`}>
                <h5 className={`text-md font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>Item Breakdown</h5>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>New Items Created:</span>
                    <span className="font-semibold text-green-600">{breakdown.newItems || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Existing Items Updated:</span>
                    <span className="font-semibold text-blue-600">{breakdown.updatedItems || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No Change Items:</span>
                    <span className="font-semibold text-yellow-600">{breakdown.noChangeItems || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Failed Items:</span>
                    <span className="font-semibold text-red-600">{breakdown.failedItems || 0}</span>
                  </div>
                </div>
              </div>

              <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'} rounded-xl p-6`}>
                <h5 className={`text-md font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>Changes Summary</h5>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Items with Changes:</span>
                    <span className="font-semibold text-purple-600">
                      {exportData.changesReport?.length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Field Changes:</span>
                    <span className="font-semibold text-purple-600">
                      {exportData.changesReport?.reduce((sum, item) => sum + (item.changes?.length || 0), 0) || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Failed Items Table */}
            {detailedResults.failedItemsList && detailedResults.failedItemsList.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
                <h4 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
                  <FaExclamationTriangle className="text-red-600" />
                  Failed Items ({detailedResults.failedItemsList.length})
                </h4>
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-red-100 sticky top-0">
                      <tr>
                        <th className="text-left p-3 font-semibold text-red-800">Row</th>
                        <th className="text-left p-3 font-semibold text-red-800">SKU Code</th>
                        <th className="text-left p-3 font-semibold text-red-800">Description</th>
                        <th className="text-left p-3 font-semibold text-red-800">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailedResults.failedItemsList.map((item, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? `${darkMode ? 'bg-gray-800' : 'bg-white'}` : 'bg-red-50'}>
                          <td className={`p-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{item.rowNumber}</td>
                          <td className={`p-3 font-mono ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.skuCode || 'N/A'}</td>
                          <td className={`p-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{item.originalData?.['SKU Description'] || 'N/A'}</td>
                          <td className="p-3 text-red-700 font-medium">{item.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={downloadImportReport}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 flex items-center gap-2 shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <FaDownload /> Download Detailed Excel Report
              </button>
              
              <button
                onClick={() => setShowResultsModal(false)}
                className="bg-gray-600 text-white py-3 px-6 rounded-lg hover:bg-gray-700 flex items-center gap-2 shadow-lg transition-all duration-200"
              >
                <FaTimes /> Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Generate bulk QR codes as ZIP (client-side, same pattern as tiles)
  const generateBulkQRZip = async () => {
    if (bulkQRItems.length === 0) {
      showToast('warning', 'No Items', 'No items found with the selected filters.');
      return;
    }

    setExportLoading(true);
    try {
      showToast('info', 'Generating QR Codes', `Creating QR codes for ${bulkQRItems.length} items...`);

      const zip = new JSZip();

      for (let i = 0; i < bulkQRItems.length; i++) {
        const item = bulkQRItems[i];
        const skuCode = item.skuCode || item._id;
        const folderName = `${(item.skuDescription || 'Item').replace(/[/\\?%*:|"<>]/g, '-').substring(0, 50)}_${skuCode}`;
        const itemFolder = zip.folder(folderName);
        const qrValue = item._id;

        // 1. PNG
        try {
          const pngDataUrl = await QRCodeLib.toDataURL(qrValue, { width: 512, margin: 2, errorCorrectionLevel: 'H', type: 'image/png' });
          itemFolder.file(`${skuCode}_QR.png`, pngDataUrl.split(',')[1], { base64: true });
        } catch (err) { console.error('Error generating PNG for', skuCode, err); }

        // 2. JPG
        try {
          const jpgDataUrl = await QRCodeLib.toDataURL(qrValue, { width: 512, margin: 2, errorCorrectionLevel: 'H', type: 'image/jpeg', quality: 0.95 });
          itemFolder.file(`${skuCode}_QR.jpg`, jpgDataUrl.split(',')[1], { base64: true });
        } catch (err) { console.error('Error generating JPG for', skuCode, err); }

        // 3. SVG
        try {
          const svgString = await QRCodeLib.toString(qrValue, { type: 'svg', width: 512, margin: 2, errorCorrectionLevel: 'H' });
          itemFolder.file(`${skuCode}_QR.svg`, svgString);
        } catch (err) { console.error('Error generating SVG for', skuCode, err); }

        // 4. PDF (QR only)
        try {
          const pdfQROnly = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [45, 65] });
          const qrDataUrl = await QRCodeLib.toDataURL(qrValue, { width: 300, margin: 1, errorCorrectionLevel: 'M' });
          pdfQROnly.addImage(qrDataUrl, 'PNG', 5, 15, 35, 35);
          itemFolder.file(`${skuCode}_QR_Only.pdf`, pdfQROnly.output('blob'));
        } catch (err) { console.error('Error generating PDF (QR only) for', skuCode, err); }

        // 5. PDF with details
        try {
          const pdfDetails = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [45, 65] });
          const qrDataUrl = await QRCodeLib.toDataURL(qrValue, { width: 300, margin: 1, errorCorrectionLevel: 'M' });
          const itemName = (item.skuDescription || 'Item').substring(0, 40);
          pdfDetails.setFontSize(9);
          pdfDetails.setFont('helvetica', 'bold');
          const nameWidth = pdfDetails.getTextWidth(itemName);
          pdfDetails.text(itemName, Math.max(2, (45 - nameWidth) / 2), 8);
          pdfDetails.addImage(qrDataUrl, 'PNG', 10, 12, 25, 25);
          pdfDetails.setFontSize(7);
          pdfDetails.setFont('helvetica', 'normal');
          const skuText = `SKU: ${skuCode}`;
          pdfDetails.text(skuText, (45 - pdfDetails.getTextWidth(skuText)) / 2, 42);
          if (item.brand?.name) { const t = item.brand.name; pdfDetails.text(t, (45 - pdfDetails.getTextWidth(t)) / 2, 47); }
          if (item.category?.name) { const t = item.category.name; pdfDetails.text(t, (45 - pdfDetails.getTextWidth(t)) / 2, 52); }
          if (item.mrp) { const t = `MRP: ${item.mrp}`; pdfDetails.text(t, (45 - pdfDetails.getTextWidth(t)) / 2, 57); }
          itemFolder.file(`${skuCode}_QR_Details.pdf`, pdfDetails.output('blob'));
        } catch (err) { console.error('Error generating PDF (with details) for', skuCode, err); }

        if (i % 10 === 0) await new Promise(resolve => setTimeout(resolve, 0));
      }

      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      saveAs(zipBlob, `Item_QR_Codes_${new Date().toISOString().split('T')[0]}.zip`);
      showToast('success', 'QR Codes Downloaded', `Generated QR codes for ${bulkQRItems.length} items.`);
      setShowBulkQRModal(false);
    } catch (error) {
      console.error('Error generating bulk QR ZIP:', error);
      showToast('error', 'QR Export Failed', error.message || 'Failed to generate QR codes.');
    } finally {
      setExportLoading(false);
    }
  };

  // Fetch items for bulk QR based on modal filters
  const fetchBulkQRItems = async () => {
    setLoadingBulkQR(true);
    try {
      const queryParams = new URLSearchParams();
      if (bulkQRFilters.brand) queryParams.append('brand', bulkQRFilters.brand);
      if (bulkQRFilters.category) queryParams.append('category', bulkQRFilters.category);
      queryParams.append('limit', '10000');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/items?${queryParams}`);
      if (!response.ok) throw new Error('Failed to fetch items');
      const result = await response.json();
      setBulkQRItems(result.success ? result.data : []);
    } catch (error) {
      console.error('Error fetching bulk QR items:', error);
      setBulkQRItems([]);
    } finally {
      setLoadingBulkQR(false);
    }
  };

  useEffect(() => {
    if (showBulkQRModal) fetchBulkQRItems();
  }, [showBulkQRModal, bulkQRFilters.brand, bulkQRFilters.category]);

  // Bulk QR Export Modal
  const renderBulkQRModal = () => {
    if (!showBulkQRModal) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={`rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className={`border-b px-6 py-4 flex justify-between items-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              <FaQrcode className="text-purple-600" />
              Export QR Codes
            </h3>
            <button onClick={() => setShowBulkQRModal(false)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}>
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-grow">
            <p className={`mb-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Select brand and/or category to generate QR codes. Leave empty to include all items.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Brand</label>
                <Select
                  placeholder="All Brands"
                  isClearable
                  value={bulkQRFilters.brand ? brands.find(b => b.value === bulkQRFilters.brand) || null : null}
                  onChange={(option) => setBulkQRFilters(prev => ({ ...prev, brand: option?.value || null }))}
                  options={brands}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? '#374151' : '#fff',
                      borderColor: darkMode ? '#4B5563' : '#D1D5DB',
                      color: darkMode ? '#fff' : '#000',
                    }),
                    menu: (base) => ({ ...base, backgroundColor: darkMode ? '#374151' : '#fff' }),
                    option: (base, { isFocused }) => ({
                      ...base,
                      backgroundColor: isFocused ? (darkMode ? '#4B5563' : '#E5E7EB') : (darkMode ? '#374151' : '#fff'),
                      color: darkMode ? '#fff' : '#000',
                    }),
                    singleValue: (base) => ({ ...base, color: darkMode ? '#fff' : '#000' }),
                    placeholder: (base) => ({ ...base, color: darkMode ? '#9CA3AF' : '#6B7280' }),
                  }}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                <Select
                  placeholder="All Categories"
                  isClearable
                  value={bulkQRFilters.category ? categories.find(c => c.value === bulkQRFilters.category) || null : null}
                  onChange={(option) => setBulkQRFilters(prev => ({ ...prev, category: option?.value || null }))}
                  options={categories}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: darkMode ? '#374151' : '#fff',
                      borderColor: darkMode ? '#4B5563' : '#D1D5DB',
                      color: darkMode ? '#fff' : '#000',
                    }),
                    menu: (base) => ({ ...base, backgroundColor: darkMode ? '#374151' : '#fff' }),
                    option: (base, { isFocused }) => ({
                      ...base,
                      backgroundColor: isFocused ? (darkMode ? '#4B5563' : '#E5E7EB') : (darkMode ? '#374151' : '#fff'),
                      color: darkMode ? '#fff' : '#000',
                    }),
                    singleValue: (base) => ({ ...base, color: darkMode ? '#fff' : '#000' }),
                    placeholder: (base) => ({ ...base, color: darkMode ? '#9CA3AF' : '#6B7280' }),
                  }}
                />
              </div>
            </div>

            <div className={`rounded-lg p-4 mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Matching items: </span>
              <span className="font-bold text-lg text-purple-600">
                {loadingBulkQR ? (
                  <span className="inline-flex items-center gap-2">
                    <FaSpinner className="animate-spin" /> Loading...
                  </span>
                ) : bulkQRItems.length}
              </span>
            </div>

            {bulkQRItems.length > 0 && (
              <div className={`border rounded-lg max-h-40 overflow-y-auto ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <div className={`p-2 border-b sticky top-0 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Preview (first 10)</span>
                </div>
                <div className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                  {bulkQRItems.slice(0, 10).map((item) => (
                    <div key={item._id} className="px-3 py-2 text-sm flex justify-between items-center">
                      <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{item.skuDescription}</span>
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{item.skuCode}</span>
                    </div>
                  ))}
                  {bulkQRItems.length > 10 && (
                    <div className={`px-3 py-2 text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      ... and {bulkQRItems.length - 10} more items
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className={`border-t px-6 py-4 flex justify-between items-center ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
            <button
              onClick={() => setBulkQRFilters({ brand: null, category: null })}
              className={`px-4 py-2 flex items-center gap-2 text-sm ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
            >
              <FaTimes className="w-4 h-4" /> Clear Filters
            </button>
            <button
              onClick={generateBulkQRZip}
              disabled={bulkQRItems.length === 0 || exportLoading}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {exportLoading ? (
                <><FaSpinner className="animate-spin" /> Generating...</>
              ) : (
                <><FaQrcode /> Generate {bulkQRItems.length} QR Codes</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto">
        <div className="mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 text-white shadow-lg">
            <h1 className="text-3xl font-bold mb-2">Items Management</h1>
            <p className="text-blue-100">Manage and import item price lists with comprehensive filtering and search capabilities</p>
          </div>
        </div>

        {/* Header Actions */}
        <div className={`rounded-xl shadow-sm border p-6 mb-8 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-4 items-center">
              {/* Search */}
              <div className="relative">
                <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder="Search SKU Code or Description..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className={`pl-10 pr-4 py-3 border rounded-lg w-72 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              {/* Brand Filter */}
              <select
                value={filters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
                className={`border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                <option value="">All Brands</option>
                {brands.map(brand => (
                  <option key={brand.value} value={brand.value}>{brand.label}</option>
                ))}
              </select>

              {/* Category Filter */}
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className={`border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                <option value="">All Categories</option>
                {reportCategoryOptions.map(category => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </select>

              {/* Series Filter */}
              <select
                value={filters.series}
                onChange={(e) => handleFilterChange('series', e.target.value)}
                className={`border rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                <option value="">All Series</option>
                <option value="RS">RS</option>
                <option value="AO">AO</option>
                <option value="SC">SC</option>
                <option value="DC">DC</option>
                <option value="ITF">ITF</option>
                <option value="OT">OT</option>
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <div className={`inline-flex self-center rounded-lg border overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-4 py-3 text-sm font-semibold ${viewMode === 'table' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700')}`}
                >
                  Table
                </button>
                <button
                  onClick={() => setViewMode('tree')}
                  className={`px-4 py-3 text-sm font-semibold ${viewMode === 'tree' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700')}`}
                  title="Group item → variants → sub-items"
                >
                  Tree
                </button>
              </div>
              <button
                onClick={() => setShowImportModal(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 flex items-center gap-2 shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <FaUpload /> Import Price List
              </button>
              <button
                onClick={handleExportPriceList}
                className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white py-3 px-6 rounded-lg hover:from-emerald-700 hover:to-emerald-800 flex items-center gap-2 shadow-lg transition-all duration-200 transform hover:scale-105"
                title="Export items with all columns (current format)"
              >
                <FaDownload /> Export Price List
              </button>
              <button
                onClick={handleExportImportFormat}
                className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-3 px-6 rounded-lg hover:from-teal-700 hover:to-teal-800 flex items-center gap-2 shadow-lg transition-all duration-200 transform hover:scale-105"
                title="Export in the import format so you can edit prices and re-import"
              >
                <FaDownload /> Export for Import
              </button>
              <button
                onClick={() => { setBulkQRFilters({ brand: null, category: null }); setShowBulkQRModal(true); }}
                className="bg-gradient-to-r from-purple-600 to-purple-700 text-white py-3 px-6 rounded-lg hover:from-purple-700 hover:to-purple-800 flex items-center gap-2 shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <FaQrcode /> Export QR Codes
              </button>
            </div>
          </div>
        </div>

        {/* Tree view: Item -> Variants -> Sub-items */}
        {viewMode === 'tree' && (
          <div className={`rounded-xl shadow-sm border overflow-hidden mb-8 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            {items.length === 0 ? (
              <div className={`px-6 py-10 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No items.</div>
            ) : (
              <div className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {items.map(item => {
                  const open = !!expandedItems[item._id];
                  const variants = item.variants || [];
                  const subs = item.subItems || [];
                  const hasChildren = variants.length > 0 || subs.length > 0;
                  return (
                    <div key={item._id}>
                      <button
                        type="button"
                        onClick={() => hasChildren && toggleItemExpanded(item._id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                      >
                        <span className={`w-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{hasChildren ? (open ? '▾' : '▸') : '•'}</span>
                        <span className={`font-mono text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>{item.skuCode || '—'}</span>
                        <span className={`flex-1 text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{item.skuDescription}</span>
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.brand?.name || ''}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${item.isActive === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{item.isActive === false ? 'Inactive' : 'Active'}</span>
                        <span className={`text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>₹{Number(item.mrp || 0).toLocaleString()}</span>
                      </button>
                      {open && (
                        <div className={`pl-10 pr-4 pb-3 space-y-2 ${darkMode ? 'bg-gray-900/30' : 'bg-gray-50/60'}`}>
                          {!hasChildren && <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No variants or sub-items.</div>}
                          {variants.length > 0 && (
                            <div>
                              <div className={`text-xs font-bold uppercase tracking-wide opacity-60 mt-2 ${darkMode ? 'text-violet-300' : 'text-violet-700'}`}>Variants</div>
                              <ul className="mt-1 space-y-1">
                                {variants.map((v, i) => (
                                  <li key={i} className={`text-sm flex flex-wrap gap-2 items-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                    <span className="font-mono text-xs opacity-60">{v.variantId || '—'}</span>
                                    <span className="font-medium">{v.name || 'Variant'}</span>
                                    <span className="opacity-70">₹{Number(v.mrp || 0).toLocaleString()}</span>
                                    {Object.entries(v.additionalFieldValues || {}).map(([k, val]) => (
                                      <span key={k} className="text-xs opacity-70">{k}: {String(val)}</span>
                                    ))}
                                    {(v.preferredSubItems || []).length > 0 && (
                                      <span className="text-xs opacity-70">
                                        prefers: {(v.preferredSubItems || []).map(s => s?.skuCode || s?.id || s?.skuDescription || '').filter(Boolean).join(', ')}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {subs.length > 0 && (
                            <div>
                              <div className={`text-xs font-bold uppercase tracking-wide opacity-60 mt-2 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>Sub-items</div>
                              <ul className="mt-1 space-y-1">
                                {subs.map((s, i) => (
                                  <li key={i} className={`text-sm flex flex-wrap gap-2 items-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                    <span className="font-medium">{s.item?.skuDescription || s.item?.skuCode || 'Sub-item'}</span>
                                    {s.item?.skuCode ? <span className="font-mono text-xs opacity-60">{s.item.skuCode}</span> : null}
                                    <span className="opacity-70">× {s.quantity || 1}</span>
                                    {s.variantName ? <span className="text-xs opacity-70">variant: {s.variantName}</span> : null}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Items Table */}
        <div className={`rounded-xl shadow-sm border overflow-hidden ${viewMode === 'tree' ? 'hidden' : ''} ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={`bg-gradient-to-r ${darkMode ? 'from-gray-700 to-gray-800' : 'from-gray-50 to-gray-100'}`}>
                <tr>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    SKU Code
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Description
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Brand
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Category
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Series
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    MRP
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    CLP
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    SDP
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    NRP
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-4 text-left text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-100'}`}>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <FaSpinner className="animate-spin text-3xl text-blue-600 mb-4" />
                        <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading items...</p>
                      </div>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                          <FaBox className="text-2xl text-gray-400" />
                        </div>
                        <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No items found</p>
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Try adjusting your filters or import new items</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((item, index) => (
                    <tr key={item._id} className={`transition-colors duration-200 ${darkMode ? 'hover:bg-blue-900/30' : 'hover:bg-blue-50'} ${index % 2 === 0 ? (darkMode ? 'bg-gray-800' : 'bg-white') : (darkMode ? 'bg-gray-700' : 'bg-gray-50')}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-bold px-3 py-1 rounded-full inline-block ${darkMode ? 'text-blue-300 bg-blue-900' : 'text-blue-900 bg-blue-100'}`}>
                          {item.skuCode}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`text-sm max-w-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          <div className="font-medium truncate" title={item.skuDescription}>
                            {item.skuDescription}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                          {item.brand?.name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                          {item.category?.name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800'}`}>
                          {item.series}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-bold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          ₹{item.mrp?.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                          ₹{item.offerType?.clp?.toLocaleString() || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                          ₹{item.offerType?.sdp?.toLocaleString() || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                          ₹{item.offerType?.nrp?.toLocaleString() || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          item.isActive === false
                            ? (darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700')
                            : (darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700')
                        }`}>
                          {item.isActive === false ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-3">
                          <button
                            onClick={() => showItemDetails(item)}
                            className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-blue-400 hover:text-blue-200 hover:bg-blue-900/40' : 'text-blue-600 hover:text-blue-900 hover:bg-blue-100'}`}
                            title="View Details"
                          >
                            <FaEye className="text-lg" />
                          </button>
                          <button
                            onClick={() => toggleItemActive(item)}
                            disabled={activeTogglingId === item._id}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              item.isActive === false
                                ? (darkMode ? 'text-green-400 hover:text-green-200 hover:bg-green-900/40' : 'text-green-600 hover:text-green-900 hover:bg-green-100')
                                : (darkMode ? 'text-red-400 hover:text-red-200 hover:bg-red-900/40' : 'text-red-600 hover:text-red-900 hover:bg-red-100')
                            }`}
                            title={item.isActive === false ? 'Activate item' : 'Deactivate item'}
                          >
                            {activeTogglingId === item._id ? <FaSpinner className="text-lg animate-spin" /> : (item.isActive === false ? <FaCheckCircle className="text-lg" /> : <FaTimes className="text-lg" />)}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Enhanced Pagination */}
          {pagination.total > 1 && (
            <div className={`bg-gradient-to-r px-6 py-4 flex items-center justify-between border-t ${darkMode ? 'from-gray-700 to-gray-800 border-gray-700' : 'from-gray-50 to-gray-100 border-gray-200'}`}>
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.current - 1)}
                  disabled={pagination.current === 1}
                  className="relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.current + 1)}
                  disabled={pagination.current === pagination.total}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Showing <span className="font-bold text-blue-600">{(pagination.current - 1) * filters.limit + 1}</span> to{' '}
                    <span className="font-bold text-blue-600">
                      {Math.min(pagination.current * filters.limit, pagination.totalItems)}
                    </span>{' '}
                    of <span className="font-bold text-blue-600">{pagination.totalItems}</span> results
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {/* First Page */}
                  {pagination.current > 3 && (
                    <>
                      <button
                        onClick={() => handlePageChange(1)}
                        className="relative inline-flex items-center px-3 py-2 border bg-white text-sm font-medium text-gray-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 rounded-md transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-blue-400"
                      >
                        1
                      </button>
                      {pagination.current > 4 && (
                        <span className={`relative inline-flex items-center px-2 py-2 text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                          ...
                        </span>
                      )}
                    </>
                  )}

                  {/* Previous Page */}
                  {pagination.current > 1 && (
                    <button
                      onClick={() => handlePageChange(pagination.current - 1)}
                      className="relative inline-flex items-center px-3 py-2 border bg-white text-sm font-medium text-gray-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 rounded-md transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-blue-400"
                    >
                      {pagination.current - 1}
                    </button>
                  )}

                  {/* Current Page */}
                  <button
                    className="relative inline-flex items-center px-4 py-2 border border-blue-600 bg-blue-600 text-sm font-medium text-white rounded-md"
                  >
                    {pagination.current}
                  </button>

                  {/* Next Page */}
                  {pagination.current < pagination.total && (
                    <button
                      onClick={() => handlePageChange(pagination.current + 1)}
                      className="relative inline-flex items-center px-3 py-2 border bg-white text-sm font-medium text-gray-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 rounded-md transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-blue-400"
                    >
                      {pagination.current + 1}
                    </button>
                  )}

                  {/* Last Page */}
                  {pagination.current < pagination.total - 2 && (
                    <>
                      {pagination.current < pagination.total - 3 && (
                        <span className={`relative inline-flex items-center px-2 py-2 text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                          ...
                        </span>
                      )}
                      <button
                        onClick={() => handlePageChange(pagination.total)}
                        className="relative inline-flex items-center px-3 py-2 border bg-white text-sm font-medium text-gray-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 rounded-md transition-colors dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-blue-400"
                      >
                        {pagination.total}
                      </button>
                    </>
                  )}

                  {/* Navigation Arrows */}
                  <div className="flex items-center space-x-1 ml-4">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.current === 1}
                      className="relative inline-flex items-center px-2 py-2 border bg-white text-sm font-medium text-gray-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 rounded-md transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-blue-400 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
                      title="First Page"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.current - 1)}
                      disabled={pagination.current === 1}
                      className="relative inline-flex items-center px-2 py-2 border bg-white text-sm font-medium text-gray-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 rounded-md transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-blue-400 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
                      title="Previous Page"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.current + 1)}
                      disabled={pagination.current === pagination.total}
                      className="relative inline-flex items-center px-2 py-2 border bg-white text-sm font-medium text-gray-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 rounded-md transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-blue-400 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
                      title="Next Page"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.total)}
                      disabled={pagination.current === pagination.total}
                      className="relative inline-flex items-center px-2 py-2 border bg-white text-sm font-medium text-gray-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 rounded-md transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-blue-400 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
                      title="Last Page"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Page Size Selector */}
                  <div className="flex items-center space-x-2 ml-4">
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Per page:</label>
                    <select
                      value={filters.limit}
                      onChange={(e) => {
                        const newLimit = parseInt(e.target.value);
                        setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));
                      }}
                      className={`border rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {renderImportModal()}
        {renderBrandSelectionModal()}
        {renderPreviewModal()}
        {renderProgressModal()}
        {renderResultsModal()}
        {renderItemDetailsModal()}
        {renderBulkQRModal()}
        {renderToast()}
      </div>
    </div>
  );
}
